/**
 * Login page — email/password sign-in form.
 * POSTs to /api/auth/signin, then invalidates the /api/me cache so AuthGate
 * picks up the new session and renders the app shell.
 */
import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { api, ApiRequestError } from '../lib/api.js';
import { Input } from '../shell/Field.js';
import { Button } from '../shell/Button.js';
import { toast } from '../shell/Toast.js';

export function LoginPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<{ ok: boolean }>('/api/auth/signin', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiRequestError
        ? err.body.message
        : 'Sign-in failed. Please try again.';
      toast('error', msg);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const submittedEmail = String(form.get('email') ?? '').trim();
    const submittedPassword = String(form.get('password') ?? '');
    if (!submittedEmail || !submittedPassword) return;
    login.mutate({ email: submittedEmail, password: submittedPassword });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-paper)] safe-px safe-pt safe-pb">
      <div className="w-full max-w-sm py-6">
        <div className="rounded-xl border border-[var(--color-rule)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-brutal-lg)] sm:p-8">
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-lg border border-[var(--color-rule)] bg-[var(--color-accent)] shadow-[var(--shadow-brutal)]">
              <ShieldCheck className="h-7 w-7 text-[var(--color-accent-ink)]" />
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-[var(--color-ink)]">Vibe Wireless HR</h1>
            <p className="mt-1 text-sm font-medium text-[var(--color-muted)]">Sign in to access your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
            <Button type="submit" className="w-full" loading={login.isPending}>
              Sign In
            </Button>
          </form>

          <p className="mt-4 text-center text-xs font-medium text-[var(--color-subtle)]">
            Contact your administrator if you need an account.
          </p>
        </div>
      </div>
    </div>
  );
}
